import { createAsyncTypes } from 'store/utils';

// State Key
export const KEY = 'deliveryLog';

// Action Types
export const types = {
  GET_DELIVERY_LOG: createAsyncTypes(`${KEY}/GET_DELIVERY_LOG`),
  RESET_DELIVERY_LOG: `${KEY}/RESET_DELIVERY_LOG`,
  RESET_EXPORT_DATA: `${KEY}/RESET_EXPORT_DATA`,
  GET_TRANSACTION_PRICE_TYPES: createAsyncTypes(`${KEY}/GET_TRANSACTION_PRICE_TYPES`),
  REFUND_TRANSACTION: createAsyncTypes(`${KEY}/REFUND_TRANSACTION`),
  VOID_TRANSACTION: createAsyncTypes(`${KEY}/VOID_TRANSACTION`),
  RECONCILE_TRANSACTIONS: createAsyncTypes(`${KEY}/RECONCILE_TRANSACTIONS`),
  GET_SETTLEMENT_REPORT: createAsyncTypes(`${KEY}/GET_SETTLEMENT_REPORT`),
  GET_SALES_REPORT: createAsyncTypes(`${KEY}/GET_SALES_REPORT`),
  GET_SALES_REPORT_BY_CARD_TYPE: createAsyncTypes(`${KEY}/GET_SALES_REPORT_BY_CARD_TYPE`),
  RESET_SALES_REPORT_BY_CARD_TYPE_DATA: `${KEY}/RESET_SALES_REPORT_BY_CARD_TYPE_DATA`,
  RESET_SALES_REPORT_DATA: `${KEY}/RESET_SALES_REPORT_DATA`,
  RESET_SETTLEMENT_REPORT_DATA: `${KEY}/RESET_SETTLEMENT_REPORT_DATA`
};

export interface Transaction {
  orderTime: string;
  id: string;
  kioskId: string;
  kioskName: string;
  areaId: string;
  areaName: string;
  venueId: string;
  venueName: string;
  flow: string;
  deliveryType: string;
  integrationOrderId: string;
  ticketCount: number;
  deliveryFailedTicketCount: number;
  amount: number;
  status: number;
  cardholderName?: string;
  cardType?: string;
  cardBin?: string;
  cardLastFour?: string;
  cardExpiry?: string;
  paymentGatewayCode?: string;
  paymentGatewaySaleReferenceNumber?: string;
  paymentGatewayRefundReferenceNumber?: string;
  paymentGatewayApprovedAmount?: number;
  transactionFee1Amount?: number;
  transactionFee1PercentageAmount?: number;
  venueTax1Label?: string;
  venueTax1PercentageAmount?: number;
  soldAt: string;
  reversedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface SettlementReportItem {
  kioskId: string;
  kioskName: string;
  venueId: string;
  venueName: string;
  areaId: string;
  areaName: string;
  taxesDeducted: number;
  venueTax1: number;
  amount: number;
  cardProcessingFeeMarkup?: number;
  transactionFee1?: number;
  productFees?: number;
  kisFees: number;
  kisFeesDeducted: number;
}

export interface SalesReportItem {
  kioskId: string;
  kioskName: string;
  venueId: string;
  venueName: string;
  areaId: string;
  areaName: string;
  amount: number;
}

export interface SalesReportTotal {
  amount: number;
}

export interface GetSalesReportResponse {
  data: SalesReportItem[];
  total: SalesReportTotal;
}

export interface GetSalesReportRequest {
  type: typeof types.GET_SALES_REPORT.REQUEST;
  payload: {
    clientIntegrationId: string;
    startTime: string;
    endTime: string;
  };
}

export interface GetSalesReportPending {
  type: typeof types.GET_SALES_REPORT.PENDING;
  payload: boolean;
}

export interface GetSalesReportSuccess {
  type: typeof types.GET_SALES_REPORT.SUCCESS;
  payload: GetSalesReportResponse;
}

export interface GetSalesReportError {
  type: typeof types.GET_SALES_REPORT.ERROR;
  payload: string;
}

export interface SalesReportByCardTypeItem {
  kioskId: string;
  kioskName: string;
  venueId: string;
  venueName: string;
  areaId: string;
  areaName: string;
  amexTransactionsCount: number;
  amexTransactionsAmount: number;
  nonAmexTransactionsCount: number;
  nonAmexTransactionsAmount: number;
}

export interface GetSalesReportByCardTypeResponse {
  data: SalesReportByCardTypeItem[];
}

export interface GetSalesReportByCardTypeRequest {
  type: typeof types.GET_SALES_REPORT_BY_CARD_TYPE.REQUEST;
  payload: {
    clientIntegrationId: string;
    startTime: string;
    endTime: string;
  };
}

export interface GetSalesReportByCardTypePending {
  type: typeof types.GET_SALES_REPORT_BY_CARD_TYPE.PENDING;
  payload: boolean;
}

export interface GetSalesReportByCardTypeSuccess {
  type: typeof types.GET_SALES_REPORT_BY_CARD_TYPE.SUCCESS;
  payload: GetSalesReportByCardTypeResponse;
}

export interface GetSalesReportByCardTypeError {
  type: typeof types.GET_SALES_REPORT_BY_CARD_TYPE.ERROR;
  payload: string;
}

export interface DeliveryLogState {
  readonly data: Transaction[];
  readonly hasMore: boolean;
  readonly pending: boolean;
  readonly error: string | null;
  readonly totalRecords: number;
  readonly priceTypes: TransactionPriceType[];
  readonly priceTypesPending: boolean;
  readonly settlementReport: {
    data: SettlementReportItem[];
    total: SettlementReportTotal | null;
    pending: boolean;
    error: string | null;
  };
  readonly salesReport: {
    data: SalesReportItem[];
    total: SalesReportTotal | null;
    pending: boolean;
    error: string | null;
  };
  readonly salesReportByCardType: {
    data: SalesReportByCardTypeItem[];
    pending: boolean;
    error: string | null;
  };
  readonly export: {
    readonly data: Transaction[];
    readonly pending: boolean;
  };
}

export interface GetDeliveryLogRequest {
  type: typeof types.GET_DELIVERY_LOG.REQUEST;
  payload: {
    page: number;
    sort: string;
    sortColumn: string;
    orderNumber: string;
    startTime: Date;
    endTime: Date;
    kioskIds: string[];
    flows: string[];
    deliveryTypes: string[];
    exportData?: boolean;
    searchValue?: string;
    clientIntegrationId: string;
  };
}

export interface TransactionPriceType {
  id: string;
  transactionId: string;
  productId: string;
  productName: string;
  priceTypeId: string;
  priceTypeName: string;
  priceTypePrice: string | null;
  productFee1Amount: string | null;
  productFee1PercentageAmount: string | null;
}

export interface SettlementReportTotal {
  taxesDeducted: number;
  venueTax1: number;
  amount: number;
  cardProcessingFeeMarkup?: number;
  transactionFee1?: number;
  productFees?: number;
  kisFees: number;
  kisFeesDeducted: number;
}

export interface GetSettlementReportResponse {
  data: SettlementReportItem[];
  total: SettlementReportTotal;
}

export interface GetSettlementReportRequest {
  type: typeof types.GET_SETTLEMENT_REPORT.REQUEST;
  payload: {
    clientIntegrationId: string;
    startTime: string;
    endTime: string;
  };
}

export interface GetSettlementReportPending {
  type: typeof types.GET_SETTLEMENT_REPORT.PENDING;
  payload: boolean;
}

export interface GetSettlementReportSuccess {
  type: typeof types.GET_SETTLEMENT_REPORT.SUCCESS;
  payload: GetSettlementReportResponse;
}

export interface GetSettlementReportError {
  type: typeof types.GET_SETTLEMENT_REPORT.ERROR;
  payload: string;
}

export interface GetDeliveryLogError {
  type: typeof types.GET_DELIVERY_LOG.ERROR;
  payload: string;
}

export interface GetDeliveryLogPending {
  type: typeof types.GET_DELIVERY_LOG.PENDING;
  payload: { pending: boolean; exportData: boolean };
}

export interface GetDeliveryLogSuccess {
  type: typeof types.GET_DELIVERY_LOG.SUCCESS;
  payload: {
    data: Transaction[];
    total: number;
    exportData: boolean;
  };
}

export interface RefundTransactionSuccess {
  type: typeof types.REFUND_TRANSACTION.SUCCESS;
  payload: Transaction;
}

export interface VoidTransactionSuccess {
  type: typeof types.VOID_TRANSACTION.SUCCESS;
  payload: Transaction;
}

export interface GetDeliveryLogResponse {
  data: Transaction[];
  total: number;
}

export interface ResetDeliveryLog {
  type: typeof types.RESET_DELIVERY_LOG;
}

export interface GetTransactionPriceTypesRequest {
  type: typeof types.GET_TRANSACTION_PRICE_TYPES.REQUEST;
  payload: {
    transactionId: string;
    clientIntegrationId: string;
  };
}

export interface GetTransactionPriceTypesSuccess {
  type: typeof types.GET_TRANSACTION_PRICE_TYPES.SUCCESS;
  payload: TransactionPriceType[];
}

export interface GetTransactionPriceTypesPending {
  type: typeof types.GET_TRANSACTION_PRICE_TYPES.PENDING;
  payload: boolean;
}

export interface ReconcileTransactionsRequest {
  type: typeof types.RECONCILE_TRANSACTIONS.REQUEST;
  payload: {
    clientIntegrationId: string;
    startTime: string;
    endTime: string;
    identifyOnly: boolean;
  };
}

export interface ReconcileTransactionsSuccess {
  type: typeof types.RECONCILE_TRANSACTIONS.SUCCESS;
}

export interface ReconcileTransactionsError {
  type: typeof types.RECONCILE_TRANSACTIONS.ERROR;
  payload: string;
}

export interface ResetExportData {
  type: typeof types.RESET_EXPORT_DATA;
}

export interface ResetSalesReportByCardTypeData {
  type: typeof types.RESET_SALES_REPORT_BY_CARD_TYPE_DATA;
}

export interface ResetSalesReportData {
  type: typeof types.RESET_SALES_REPORT_DATA;
}

export interface ResetSettlementReportData {
  type: typeof types.RESET_SETTLEMENT_REPORT_DATA;
}

export type DeliveryLogActionTypes =
  | GetDeliveryLogRequest
  | GetDeliveryLogSuccess
  | GetDeliveryLogError
  | GetDeliveryLogPending
  | ResetDeliveryLog
  | GetTransactionPriceTypesRequest
  | GetTransactionPriceTypesSuccess
  | GetTransactionPriceTypesPending
  | RefundTransactionSuccess
  | VoidTransactionSuccess
  | ReconcileTransactionsRequest
  | ReconcileTransactionsSuccess
  | ReconcileTransactionsError
  | GetSettlementReportRequest
  | GetSettlementReportSuccess
  | GetSettlementReportPending
  | GetSettlementReportError
  | GetSalesReportRequest
  | GetSalesReportSuccess
  | GetSalesReportPending
  | GetSalesReportError
  | GetSalesReportByCardTypeRequest
  | GetSalesReportByCardTypeSuccess
  | GetSalesReportByCardTypePending
  | GetSalesReportByCardTypeError
  | ResetExportData
  | ResetSalesReportByCardTypeData
  | ResetSalesReportData
  | ResetSettlementReportData;
